"use client";

import { HTMLAttributes } from "react";

type SkeletonVariant = "text" | "circular" | "rectangular";

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  lines?: number;
}

function Skeleton({
  variant = "text",
  width,
  height,
  lines = 1,
  className = "",
  style,
  ...props
}: SkeletonProps) {
  const baseStyles = "skeleton";
  
  const variantStyles: Record<SkeletonVariant, string> = {
    text: "h-4 rounded",
    circular: "rounded-full",
    rectangular: "rounded-lg",
  };

  const computedStyle = {
    width: width ? (typeof width === "number" ? `${width}px` : width) : undefined,
    height: height ? (typeof height === "number" ? `${height}px` : height) : undefined,
    ...style,
  };

  if (variant === "text" && lines > 1) {
    return (
      <div className={`space-y-2 ${className}`} {...props}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={`${baseStyles} ${variantStyles.text}`}
            style={{
              ...computedStyle,
              width: index === lines - 1 ? "75%" : computedStyle.width,
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      style={computedStyle}
      {...props}
    />
  );
}

// Pre-built skeleton layouts
function SkeletonCard() {
  return (
    <div className="p-4 space-y-4">
      <Skeleton variant="rectangular" height={120} />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" lines={2} />
    </div>
  );
}

function SkeletonListItem() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" width="40%" />
        <Skeleton variant="text" width="60%" />
      </div>
    </div>
  );
}

function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex gap-4">
          <Skeleton variant="text" width="25%" />
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="20%" />
          <Skeleton variant="text" width="20%" />
        </div>
      ))}
    </div>
  );
}

export {
  Skeleton,
  SkeletonCard,
  SkeletonListItem,
  SkeletonTable,
  type SkeletonProps,
};
